import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { List } from "../../schema/list";

/**
 * The source arrays whose entries the ListEditSource page can open for editing (the `selected`
 * union) — tracker files and LinkedIn-profile scrapes are listed on the page but not editable
 * through `selected`, so they are not deep-linkable.
 */
const collectEditableSources = (list: List) => [
	...(list.maps || []),
	...(list.jobs || []),
	...(list.csvEnrichments || []),
	...(list.liEvents || []),
	...(list.liCompanyActivity || []),
	...(list.liPosts || []),
	...(list.liProfileVisits || []),
	...(list.liPeople || []),
	...(list.liSn || []),
	...(list.parallelAi || []),
	...(list.customEvents || []),
	...(list.fundingMa || []),
	...(list.swarms || []),
	...(list.companyEnriches || []),
];

export type EditableListSource = ReturnType<typeof collectEditableSources>[number];

/**
 * Deep-link support for `/app/lists/create/source/:id?editSource=<sourceId>` (PREC-3703): once the
 * list has loaded, selects the referenced source for editing (the Sources header dropdown links
 * here per row). The param is consumed one-shot and removed from the URL, so closing the editor
 * doesn't re-open it.
 */
export const useEditSourceParam = (
	list: List | undefined,
	onSelect: (source: EditableListSource) => void
): void => {
	const [searchParams, setSearchParams] = useSearchParams();
	const appliedRef = useRef(false);
	const editSourceId = searchParams.get("editSource");

	useEffect(() => {
		if (!list || !editSourceId || appliedRef.current) {
			return;
		}

		appliedRef.current = true;

		const source = collectEditableSources(list).find((item) => item.id === Number(editSourceId));

		if (source) {
			onSelect(source);
		}

		const next = new URLSearchParams(searchParams);

		next.delete("editSource");
		setSearchParams(next, { replace: true });
	}, [list, editSourceId, onSelect, searchParams, setSearchParams]);
};
